import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Card,
  Typography,
  Input,
  Textarea,
  Select,
  Option,
  Checkbox,
  Radio,
  RadioGroup,
  Button,
  FormControl,
  FormLabel,
  Stack,
  Alert,
  CircularProgress,
  Divider,
} from "@mui/joy";
import {
  formsApi,
  publicFormApi,
  type Form,
  type FormField,
} from "../../lib/apiMarketing";
import { applyEmbedBrandTheme, reportEmbedState } from "../../lib/embedTheme";

export const FormEmbed: React.FC<{ previewForm?: Form }> = ({
  previewForm,
}) => {
  const { formId } = useParams<{ formId: string }>();
  const [form, setForm] = useState<Form | null>(previewForm || null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Brand colors arrive as query params from generated websites so the form
  // matches the site, not ApexOS. ?title=0 hides the form's internal name
  // (the embedding page supplies its own heading).
  const hideTitle =
    new URLSearchParams(window.location.search).get("title") === "0";
  useEffect(() => {
    applyEmbedBrandTheme();
  }, []);

  // Report our rendered height so the embedding iframe auto-sizes (the
  // embed script has always listened for apexos-form-resize; this is the
  // sender side).
  useEffect(() => {
    if (loading || !formId) return;
    return reportEmbedState({ type: "apexos-form-resize", formId });
  }, [loading, submitted, formId]);

  useEffect(() => {
    const fetchForm = async () => {
      if (!formId && !previewForm) return;

      try {
        setLoading(true);
        const response = previewForm || (await publicFormApi.getForm(formId!));
        setForm(response);

        // Initialize form data with default values
        const initialData: Record<string, any> = {};
        if (response.form_schema?.fields) {
          response.form_schema.fields.forEach((field: FormField) => {
            if (field.default_value) {
              initialData[field.name] = field.default_value;
            } else if (field.field_type === "checkbox") {
              initialData[field.name] = false;
            } else if (field.field_type === "multi_checkbox") {
              initialData[field.name] = [];
            } else {
              initialData[field.name] = "";
            }
          });
        }
        setFormData(initialData);
      } catch (err) {
        console.error("Error fetching form:", err);
        setError("Form not found or not available");
      } finally {
        setLoading(false);
      }
    };

    fetchForm();
  }, [formId, previewForm]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form || (!formId && !previewForm)) return;

    try {
      setSubmitting(true);
      setSubmitError(null);
      if (previewForm) {
        const result = await formsApi.testSubmission(
          previewForm.id,
          previewForm.form_schema,
          formData,
        );
        setSubmitError(result.data.message);
        return;
      }
      await publicFormApi.submitForm({
        form_id: formId!,
        data: formData,
        page_url: window.location.href,
        utm_source:
          new URLSearchParams(window.location.search).get("utm_source") ||
          undefined,
        utm_medium:
          new URLSearchParams(window.location.search).get("utm_medium") ||
          undefined,
        utm_campaign:
          new URLSearchParams(window.location.search).get("utm_campaign") ||
          undefined,
        utm_term:
          new URLSearchParams(window.location.search).get("utm_term") ||
          undefined,
        utm_content:
          new URLSearchParams(window.location.search).get("utm_content") ||
          undefined,
      });

      setSubmitted(true);

      // Handle submit action
      if (form.submit_action === "redirect" && form.submit_action_value) {
        window.location.href = form.submit_action_value;
      }
    } catch (err) {
      console.error("Error submitting form:", err);
      setSubmitError(
        err instanceof Error
          ? err.message
          : "Failed to submit form. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field: FormField) => {
    const commonProps = {
      value: formData[field.name] ?? "",
      onChange: (e: any) => {
        const value =
          field.field_type === "checkbox" ? e.target.checked : e.target.value;
        setFormData((prev) => ({ ...prev, [field.name]: value }));
      },
      required: field.required,
      placeholder: field.placeholder,
    };

    switch (field.field_type) {
      case "text":
      case "email":
      case "phone":
      case "number":
        return (
          <Input
            {...commonProps}
            type={field.field_type === "phone" ? "tel" : field.field_type}
          />
        );

      case "textarea":
        return <Textarea {...commonProps} minRows={3} />;

      case "select":
        return (
          <Select
            value={formData[field.name] || ""}
            onChange={(_, value) =>
              setFormData((prev) => ({ ...prev, [field.name]: value }))
            }
            required={field.required}
            placeholder={field.placeholder || "Select an option"}
          >
            {field.options?.map((option) => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        );

      case "radio":
        return (
          <RadioGroup
            value={formData[field.name] || ""}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, [field.name]: e.target.value }))
            }
          >
            {field.options?.map((option) => (
              <Radio
                required={field.required}
                key={option.value}
                value={option.value}
                label={option.label}
              />
            ))}
          </RadioGroup>
        );

      case "checkbox":
        return (
          <Checkbox
            required={field.required}
            checked={formData[field.name] || false}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                [field.name]: e.target.checked,
              }))
            }
            label={field.label}
          />
        );

      case "multi_checkbox":
        return (
          <Stack spacing={1}>
            {field.options?.map((option) => (
              <Checkbox
                key={option.value}
                checked={formData[field.name]?.includes(option.value) || false}
                onChange={(e) => {
                  const currentValues = formData[field.name] || [];
                  const newValues = e.target.checked
                    ? [...currentValues, option.value]
                    : currentValues.filter((v: string) => v !== option.value);
                  setFormData((prev) => ({ ...prev, [field.name]: newValues }));
                }}
                label={option.label}
              />
            ))}
          </Stack>
        );

      case "date":
        return <Input {...commonProps} type="date" />;

      case "time":
        return <Input {...commonProps} type="time" />;

      case "datetime":
        return <Input {...commonProps} type="datetime-local" />;

      case "hidden":
        return (
          <input
            type="hidden"
            name={field.name}
            value={formData[field.name] ?? ""}
          />
        );

      case "heading":
        return (
          <Typography level="h4" sx={{ mt: 2, mb: 1 }}>
            {field.label}
          </Typography>
        );

      case "paragraph":
        return (
          <Typography level="body-md" sx={{ mb: 2 }}>
            {field.label}
          </Typography>
        );

      case "divider":
        return <Divider sx={{ my: 2 }} />;

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box
        id="apexos-embed-content"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 200,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box id="apexos-embed-content" sx={{ p: 2 }}>
        <Alert color="danger">{error}</Alert>
      </Box>
    );
  }

  if (!form) {
    return (
      <Box id="apexos-embed-content" sx={{ p: 2 }}>
        <Alert color="warning">Form not found</Alert>
      </Box>
    );
  }

  if (submitted) {
    return (
      <Box id="apexos-embed-content" sx={{ p: 2 }}>
        <Card>
          <Typography level="h4" sx={{ mb: 2 }}>
            Thank You!
          </Typography>
          <Typography>
            {form.success_message || "Your submission has been received."}
          </Typography>
          {form.submit_action === "download" &&
            /^https?:\/\//i.test(form.submit_action_value || "") && (
              <Button
                component="a"
                href={form.submit_action_value}
                target="_blank"
                rel="noopener noreferrer"
              >
                Download file
              </Button>
            )}
        </Card>
      </Box>
    );
  }

  const fields = form.form_schema?.fields || [];

  return (
    <Box
      id="apexos-embed-content"
      sx={{ p: { xs: 0.75, sm: 2 }, maxWidth: 800, mx: "auto" }}
    >
      <Card>
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            {previewForm && (
              <Alert color="primary">
                Test mode: no leads, submissions, or emails are created.
              </Alert>
            )}
            {form.name && !hideTitle && (
              <Typography level="h3">{form.name}</Typography>
            )}

            {form.description && !hideTitle && (
              <Typography level="body-lg">{form.description}</Typography>
            )}

            {fields.map((field, index) => {
              if (field.field_type === "hidden")
                return (
                  <React.Fragment key={field.id || index}>
                    {renderField(field)}
                  </React.Fragment>
                );
              // Skip non-input field types for FormControl wrapper
              if (
                ["heading", "paragraph", "divider"].includes(field.field_type)
              ) {
                return <Box key={field.id || index}>{renderField(field)}</Box>;
              }

              // Skip checkbox as it has its own label
              if (field.field_type === "checkbox") {
                return (
                  <FormControl key={field.id || index}>
                    {renderField(field)}
                  </FormControl>
                );
              }

              return (
                <FormControl key={field.id || index} required={field.required}>
                  <FormLabel>{field.label}</FormLabel>
                  {renderField(field)}
                  {field.help_text && (
                    <Typography level="body-sm" sx={{ mt: 0.5 }}>
                      {field.help_text}
                    </Typography>
                  )}
                </FormControl>
              );
            })}

            <Button
              type="submit"
              size="lg"
              loading={submitting}
              disabled={submitting}
            >
              {form.submit_button_text || "Submit"}
            </Button>

            {submitError && (
              <Alert color={previewForm ? "primary" : "danger"}>
                {submitError}
              </Alert>
            )}
          </Stack>
        </form>
      </Card>
    </Box>
  );
};
